"use client";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { styled, type Theme } from "@mui/material/styles";
import { reportCmsContentIssue } from "@portal/app/_common/lib/reportCmsContentIssue";
import {
  hasFormFunnelRichTextContent,
  isSafeFormFunnelContentHref,
} from "@portal/app/_common/lib/formFunnelContent";
import type {
  FooterBlock,
  FooterFinePrintBlock,
  FooterImageBlock,
  FooterImageRowBlock,
  FooterLayoutMode,
  FooterLinkRowBlock,
  FooterSpacerBlock,
  FooterTestimonialBlock,
  FooterTextBlock,
  PayloadFooterConfig,
  PayloadMediaUpload,
} from "@portal/app/_server/services/formFunnel/payloadTypes";
import { FormFunnelRichText } from "./FormFunnelRichText";
import { getFormFunnelImageProps } from "./formFunnelMedia";

interface FormFunnelFooterProps {
  footerConfig: PayloadFooterConfig;
  variant?: "stacked" | "column";
  /** Adjust block spacing and widths for the funnel-wide split layout. */
  desktopSidebar?: boolean;
}

const TextBlock = ({ content, richContent, isColumn }: FooterTextBlock & { isColumn: boolean }) => {
  if (hasFormFunnelRichTextContent(richContent)) {
    return (
      <FormFunnelRichText content={richContent} appearance={isColumn ? "article" : "footer"} />
    );
  }

  return content?.trim() ? (
    <Typography
      color="text.secondary"
      variant={isColumn ? "body1" : "body2"}
      sx={{
        textAlign: isColumn ? "left" : "center",
        whiteSpace: "pre-line",
      }}
    >
      {content}
    </Typography>
  ) : null;
};

const ImageBlock = ({ image, url, alt, width, height }: FooterImageBlock) => {
  const imageProps = getFormFunnelImageProps(image ?? url, alt);
  if (!imageProps) return null;

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <img
        src={imageProps.src}
        alt={imageProps.alt}
        width={width ?? imageProps.width}
        height={height ?? imageProps.height}
        style={{
          display: "block",
          maxWidth: "100%",
          height: "auto",
          objectFit: "contain",
        }}
      />
    </Box>
  );
};

const LinkedImage = ({ image, link }: { image: PayloadMediaUpload | string; link?: string }) => {
  const imageProps = getFormFunnelImageProps(image);
  if (!imageProps) return null;

  const content = (
    <img
      src={imageProps.src}
      alt={imageProps.alt}
      width={imageProps.width}
      height={imageProps.height}
      style={{
        display: "block",
        width: "100%",
        maxHeight: 180,
        height: "auto",
        objectFit: "contain",
      }}
    />
  );

  return link && isSafeFormFunnelContentHref(link) ? (
    <Link href={link} sx={{ display: "block" }}>
      {content}
    </Link>
  ) : (
    content
  );
};

const ImageRowBlock = ({ columns, images }: FooterImageRowBlock) => {
  const desktopColumns = Number(columns);

  return (
    <Box
      data-testid="form-funnel-image-row"
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          md: `repeat(${desktopColumns}, minmax(0, 1fr))`,
        },
        alignItems: "center",
        gap: { xs: 2, md: 3 },
        width: "100%",
      }}
    >
      {images.map((item, index) => (
        <LinkedImage key={item.id ?? index} image={item.image} link={item.link} />
      ))}
    </Box>
  );
};

const FinePrintBlock = ({ content, collapsible, collapsedLabel }: FooterFinePrintBlock) => {
  if (!collapsible) {
    return <FormFunnelRichText content={content} tone="finePrint" />;
  }

  return (
    <Disclosure>
      <DisclosureLabel>{collapsedLabel?.trim() || "Sponsored Schools Disclosure"}</DisclosureLabel>
      <Box sx={{ mt: 1.5 }}>
        <FormFunnelRichText content={content} tone="finePrint" />
      </Box>
    </Disclosure>
  );
};

const LinkRowBlock = ({ links }: FooterLinkRowBlock) => {
  const safeLinks = links.filter(
    (link) => link.label?.trim() && isSafeFormFunnelContentHref(link.url)
  );
  if (!safeLinks.length) return null;

  return (
    <Box
      component="nav"
      aria-label="Page links"
      data-testid="form-funnel-link-row"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        columnGap: 3,
        rowGap: 1.25,
        width: "100%",
      }}
    >
      {safeLinks.map((link, index) => (
        <Link
          key={link.id ?? `${link.label}-${index}`}
          href={link.url}
          color="text.primary"
          underline="hover"
          sx={{ fontSize: "0.875rem", fontWeight: 600 }}
        >
          {link.label}
        </Link>
      ))}
    </Box>
  );
};

const spacerHeights: Record<FooterSpacerBlock["size"], number> = {
  small: 16,
  medium: 32,
  large: 64,
};

const TestimonialBlock = ({ name, quote, photo, image }: FooterTestimonialBlock) => {
  const trimmedName = name.trim();
  const initial = trimmedName.charAt(0).toUpperCase();
  const photoProps = getFormFunnelImageProps(photo ?? image, trimmedName);

  return (
    <TestimonialCard>
      <TestimonialAvatar data-testid="testimonial-avatar">
        {photoProps ? (
          <img
            src={photoProps.src}
            alt={photoProps.alt}
            width={48}
            height={48}
            style={{ borderRadius: "50%", objectFit: "cover" }}
          />
        ) : (
          initial
        )}
      </TestimonialAvatar>
      <Typography
        sx={{
          color: (theme) => theme.palette.dream.grayMediumDark,
          fontSize: "0.92rem",
          fontStyle: "italic",
          lineHeight: 1.5,
          mb: "10px",
          textAlign: "center",
        }}
      >
        &ldquo;{quote}&rdquo;
      </Typography>
      <Typography
        sx={{
          color: (theme) => theme.palette.dream.grayDark,
          fontSize: "0.85rem",
          fontWeight: 700,
          textAlign: "center",
        }}
      >
        {name}
      </Typography>
    </TestimonialCard>
  );
};

const renderBlock = (block: FooterBlock, index: number, isColumn: boolean) => {
  const key = block.id || index;

  switch (block.blockType) {
    case "text":
      if (!hasFormFunnelRichTextContent(block.richContent) && !block.content?.trim()) return null;
      return (
        <BlockItem key={key}>
          <TextBlock {...block} isColumn={isColumn} />
        </BlockItem>
      );
    case "image":
      return (
        <BlockItem key={key}>
          <ImageBlock {...block} />
        </BlockItem>
      );
    case "imageRow":
      return (
        <BlockItem key={key}>
          <ImageRowBlock {...block} />
        </BlockItem>
      );
    case "finePrint":
      return (
        <BlockItem key={key}>
          <FinePrintBlock {...block} />
        </BlockItem>
      );
    case "linkRow":
      return (
        <BlockItem key={key}>
          <LinkRowBlock {...block} />
        </BlockItem>
      );
    case "divider":
      return <Divider key={key} flexItem />;
    case "spacer":
      return <Box key={key} aria-hidden sx={{ height: spacerHeights[block.size] }} />;
    case "testimonial":
      return <TestimonialBlock key={key} {...block} />;
    default: {
      // Only reachable when the CMS gains a block type this renderer lacks.
      const unknownBlock = block as { blockType?: unknown; id?: string | null };
      const blockType = String(unknownBlock.blockType);
      reportCmsContentIssue({
        source: "FormFunnelFooter",
        collection: "form-funnels",
        issue: "unsupported_footer_block",
        value: blockType,
        identifier: unknownBlock.id ?? undefined,
        message: `[FormFunnelFooter] Unsupported footer block type "${blockType}"`,
      });
      return null;
    }
  }
};

const layoutStyles: Record<FooterLayoutMode, Record<string, unknown>> = {
  vertical: {
    flexDirection: "column",
    alignItems: "stretch",
  },
  horizontal: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "flex-start",
    "& > *": { flex: "1 1 0", minWidth: 180 },
  },
  grid: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
    alignItems: "start",
  },
};

const layoutMaxWidths: Record<FooterLayoutMode, number> = {
  vertical: 600,
  horizontal: 900,
  grid: 900,
};

export function FormFunnelFooter({
  footerConfig,
  variant = "stacked",
  desktopSidebar = false,
}: FormFunnelFooterProps) {
  if (!footerConfig.enabled || !footerConfig.blocks?.length) return null;

  const mode = footerConfig.layoutMode ?? "vertical";
  const isColumn = variant === "column";

  return (
    <PageContentRoot
      data-testid="form-funnel-footer"
      data-layout-variant={variant}
      data-layout-mode={mode}
      sx={
        isColumn
          ? { p: 0, m: 0, border: 0, bgcolor: "transparent" }
          : {
              mt: 4,
              px: 2.5,
              pt: 6,
              pb: 4.5,
              borderTop: 1,
              borderColor: "dream.borderColor",
              bgcolor: "dream.grayLighter",
            }
      }
    >
      <FooterInner
        sx={{
          maxWidth: isColumn ? "none" : layoutMaxWidths[mode],
        }}
      >
        {footerConfig.title && (
          <Typography
            component={isColumn ? "h2" : "h3"}
            sx={{
              color: (theme) =>
                isColumn ? theme.palette.primary.main : theme.palette.dream.grayDark,
              fontSize: isColumn ? "1.5rem" : "1.05rem",
              fontWeight: 700,
              lineHeight: isColumn ? 1.3 : 1.4,
              mb: 2.5,
              textAlign: isColumn ? "left" : "center",
            }}
          >
            {footerConfig.title}
          </Typography>
        )}
        <FooterBlocks
          sx={[
            {
              ...layoutStyles[mode],
              gap: isColumn ? "20px" : "12px",
              ...(!isColumn && mode === "vertical" ? { alignItems: "center" } : {}),
              ...(!isColumn && mode === "grid"
                ? { gridTemplateColumns: "repeat(2, 1fr)", justifyItems: "center" }
                : {}),
            },
            ...(desktopSidebar
              ? [
                  (theme: Theme) => ({
                    // Fit three stats inside the 600px mobile container and its gutters.
                    [theme.breakpoints.up("sm")]: {
                      ...(mode === "horizontal" ? { "&& > *": { minWidth: 160 } } : {}),
                    },
                    [theme.breakpoints.up("md")]: {
                      gap: "20px",
                      alignItems: "stretch",
                      ...(mode === "horizontal"
                        ? { overflowWrap: "anywhere", "&& > *": { minWidth: 72 } }
                        : {}),
                      ...(mode === "grid"
                        ? { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }
                        : {}),
                    },
                  }),
                ]
              : []),
          ]}
        >
          {footerConfig.blocks.map((block, index) => renderBlock(block, index, isColumn))}
        </FooterBlocks>
      </FooterInner>
    </PageContentRoot>
  );
}

const PageContentRoot = styled(Box)`
  width: 100%;
`;

const FooterInner = styled(Box)`
  width: 100%;
  margin: 0 auto;
`;

const FooterBlocks = styled(Box)`
  width: 100%;
  display: flex;
  gap: 20px;
`;

const BlockItem = styled(Box)`
  width: 100%;
  min-width: 0;
`;

const Disclosure = styled("details")`
  width: 100%;
  color: ${({ theme }) => theme.palette.dream.grayMediumDark};
`;

const DisclosureLabel = styled("summary")`
  cursor: pointer;
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: 0.82rem;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const TestimonialCard = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 18px;
  background-color: ${({ theme }) => theme.palette.common.white};
  border: 1px solid ${({ theme }) => theme.palette.dream.borderColor};
  border-radius: 20px;
  width: 100%;
`;

const TestimonialAvatar = styled(Box)`
  width: 48px;
  height: 48px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 50%;
  background-color: #e8f2ff;
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: 1.1rem;
  font-weight: 700;
`;
